import { useReactiveVar } from '@apollo/client';
import IconSuccessSolid from '@apollo/icons/default/IconSuccessSolid.svg';
import { Code, Link } from '@apollo/orbit';
import classnames from 'classnames';
import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import { PermissionGuard } from 'src/components/permissionGuards/permissionGuard/PermissionGuard';
import { apiKeyGeneratedVar } from 'src/components/userApiKeysModal/UserApiKeysModal';
import { ampli } from 'src/lib/analytics/amplitude/vendor';
import Config from 'src/lib/config';
import { useDemoSupergraphInEnterpriseTrial } from 'src/lib/featureFlags';
import { DocsRouter } from 'src/lib/routers/docs';
import { setOverlayVisibleLocation } from 'src/lib/routing';

import { CodeTabBlock } from './components/CodeTabBlock';
import { DemoCallToAction } from './components/ctaButtons/DemoCTA';
import { GraphQLCallToAction } from './components/ctaButtons/GraphQLCTA';
import { LegacyCallToAction } from './components/ctaButtons/LegacyCTA';
import { RoverCallToAction } from './components/ctaButtons/RoverCTA';
import { GraphQLAnimation } from './components/GraphQLAnimation';
import { ReadOnlyLayout } from './components/ReadOnlyLayout';
import { Step, StepContent } from './components/StepContent';

/**
 * Layout for empty graph state with step navigation
 */
export const EmptyGraphLayout = ({
  accountId,
  canHaveConnectors,
  planName,
}: {
  accountId: string;
  canHaveConnectors: boolean;
  planName: string | undefined;
}) => {
  const location = useLocation();

  // Tracks if user has generated an API key. Displays success message if true
  const keyGeneratedSuccessfully = useReactiveVar(apiKeyGeneratedVar);

  const shouldShowDemoSupergraphQuickstartCTA =
    useDemoSupergraphInEnterpriseTrial();

  // Analytics: Record home page view
  useEffect(() => {
    ampli.homePageViewed({
      'Total Graphs': 0,
      'Deployed Graphs': 0,
      'Unconfigured Graphs': 0,
    });
  }, []);

  // Determine which CTAs to show based on feature flags and capabilities
  const renderCTAs = () => {
    // Free Tier users (can have connectors)
    if (canHaveConnectors) {
      // Return both a primary CTA and the GraphQL CTA
      return {
        primaryCTA: (
          <div className="my-2 flex w-full flex-col gap-4 md:w-3/4 md:flex-row lg:my-6">
            <RoverCallToAction />
          </div>
        ),
        showGraphQLCTA: true,
      };
    }

    // Non-Free Tier users with demo supergraph quickstart
    if (shouldShowDemoSupergraphQuickstartCTA) {
      // For demo supergraph, we don't show a separate GraphQL CTA to avoid too many buttons
      return {
        primaryCTA: <DemoCallToAction planName={planName} />,
        showGraphQLCTA: false,
      };
    }

    // Default case (Legacy CTA for non-free tier without demo)
    // No need for a separate GraphQL CTA here either
    return {
      primaryCTA: <LegacyCallToAction planName={planName} />,
      showGraphQLCTA: false,
    };
  };

  // Get the CTAs to render
  const { primaryCTA, showGraphQLCTA } = renderCTAs();

  return (
    <PermissionGuard
      accountId={accountId}
      accountPermissions="canCreateService"
      fallback={<ReadOnlyLayout />}
    >
      <div
        className="flex size-full flex-row bg-primary"
        data-testid="empty-graph-layout"
      >
        {/* Left pane - hidden on small screens */}
        <div className="absolute -left-full size-0 items-start justify-center overflow-hidden md:static md:left-auto md:ml-6 md:mt-12 md:flex md:size-auto md:w-1/3 md:shrink-0">
          <div className="flex flex-col gap-8">
            <GraphQLAnimation />
            <div className="flex flex-col gap-3">
              <h2 className="text-heading-2xl">Welcome to GraphOS Studio</h2>
              <p>To get started, let's first set up your workspace.</p>
            </div>
          </div>
        </div>

        {/* Right pane - full width on small screens */}
        <div className="flex size-full flex-1 flex-col overflow-y-scroll">
          {/* Header with animation  - only shown on small screens */}
          <div className="mb-6 mt-10 block w-full md:absolute md:-left-full md:size-0 md:overflow-hidden">
            <div className="flex flex-col items-center gap-8">
              <GraphQLAnimation />
              <div className="flex flex-col gap-3">
                <h2 className="text-heading-2xl">Welcome to GraphOS Studio</h2>
                <p>To get started, let's first set up your workspace.</p>
              </div>
            </div>
          </div>

          <StepContent>
            <Step id="install-rover-cli">
              <div className="flex flex-col gap-4">
                <h3 className="text-heading-xl pt-1">Install the Rover CLI</h3>
                <p>
                  The Rover CLI is the primary command-line interface for
                  GraphOS.
                </p>
                <CodeTabBlock
                  tabs={[
                    {
                      content:
                        'curl -sSL https://rover.apollo.dev/nix/latest | sh',
                      tabLabel: 'Linux / MacOS',
                    },
                    {
                      content: `iwr 'https://rover.apollo.dev/win/latest' | iex`,
                      tabLabel: 'Windows',
                    },
                  ]}
                  onCodeClick={() => {
                    ampli.cliCommandCopied({
                      CommandType: 'Onboarding',
                      CommandContent: 'rover install',
                      Location: 'ZeroState',
                    });
                  }}
                />

                <Link
                  isExternal
                  className="ml-1"
                  onClick={() => {
                    // Analytics: Record click on installation methods
                    // This appears as the event `"Other Rover Installation Methods" Clicked` in Amplitude.
                    // TODO: Merge this event with similar-named event in amplitude through a Transformation.
                    // See https://apollographql.atlassian.net/browse/ONB-635
                    ampli.roverInstallationMethodsClicked();
                  }}
                  to={DocsRouter.path('RoverInstallationMethods')}
                >
                  See other installation methods.
                </Link>
              </div>
            </Step>
            <Step id="authenticate-rover">
              <div className="flex flex-col gap-4">
                <h3 className="text-heading-xl pt-1">Authenticate</h3>
                <Code
                  canCopy
                  onClick={() => {
                    ampli.cliCommandCopied({
                      CommandType: 'Onboarding',
                      CommandContent: 'rover config auth',
                      Location: 'ZeroState',
                    });
                  }}
                  className="h-12 w-full justify-between p-3"
                >
                  rover config auth
                </Code>
                <p>
                  You'll need to give Rover a{' '}
                  <Link
                    to={setOverlayVisibleLocation({
                      location,
                      overlayName: Config.modals.newUserApiKey,
                    })}
                    onClick={() => {
                      // Analytics: Record opening of modal
                      // This appears as the event `"Generate API Key" Opened` in Amplitude.
                      // TODO: Merge this event with similar-named event in amplitude through a Transformation.
                      // See https://apollographql.atlassian.net/browse/ONB-635
                      ampli.modalOpened({
                        ModalName: Config.modals.newUserApiKey,
                      });
                    }}
                  >
                    personal API key
                  </Link>
                  .
                </p>
                {keyGeneratedSuccessfully ? (
                  <div className="flex flex-row items-center gap-2">
                    <IconSuccessSolid className="text-icon-success" />
                    <p className="text-body-sm">
                      Key was generated successfully. You can manage this type
                      of API key in your personal settings.
                    </p>
                  </div>
                ) : null}
              </div>
            </Step>
            <Step id="select-starting-point">
              <div>
                <h3 className="text-heading-xl pt-1">
                  Select your starting point
                </h3>
                <div
                  className={classnames(
                    'flex flex-col lg:flex-row lg:gap-4',
                    !showGraphQLCTA && 'mt-6',
                  )}
                >
                  {primaryCTA}
                  {showGraphQLCTA && (
                    <div className="my-2 flex w-full flex-col gap-4 md:w-3/4 md:flex-row lg:my-6">
                      <GraphQLCallToAction planName={planName} />
                    </div>
                  )}
                </div>
              </div>
            </Step>
          </StepContent>
        </div>
      </div>
    </PermissionGuard>
  );
};
